import type { AmplitudeClient, Callback } from 'amplitude-js';
import { ApiService } from '@/services/apiService';
import { CookiesStorageService } from '@/services/cookiesStorageService';
import { LocalStorageService } from '@/services/localStorageService';
import { getAnalyticsUserProperties } from '@/helpers/getAnalyticsUserProperties';
import { getChosenCookies } from '@/helpers/getChosenCookies';
import { COOKIES_KEYS, ENV, LOCAL_STORAGE_KEYS } from '@/constants';

const AMPLITUDE_HTTP_API_URL = 'https://api2.amplitude.com/2/httpapi';

// Delivers a prebuilt Amplitude HTTP API payload in a way that survives an
// immediate navigation/unload (CTA redirect to the funnel).
//
// We intentionally do NOT use navigator.sendBeacon: it always sends with
// credentials, and Amplitude requires Content-Type application/json (a
// non-simple type) which triggers a CORS preflight. Amplitude answers the
// preflight with a wildcard Access-Control-Allow-Origin, which the browser
// rejects for credentialed requests — so the beacon is silently dropped.
//
// fetch with keepalive survives navigation just like sendBeacon (it's the same
// transport Amplitude's Browser SDK 2 uses), and credentials: 'omit' makes the
// request non-credentialed, so the wildcard CORS response is accepted.
function postToAmplitudeApi(
	body: string,
	errorLabel: string,
	onSuccess?: () => void,
) {
	void fetch(AMPLITUDE_HTTP_API_URL, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body,
		keepalive: true,
		credentials: 'omit',
		mode: 'cors',
	})
		.then(response => {
			// Only run onSuccess on a real 2xx. If the page navigates before this
			// resolves, the callback never fires — that's the desired behavior:
			// the pending-event cookie stays, and the funnel re-sends with the
			// same insert_id (Amplitude dedups if our request actually arrived).
			if (response.ok && onSuccess) onSuccess();
		})
		.catch(() => console.log(`Amplitude Error in ${errorLabel}`));
}

let amplitudeMod: any | null = null;
let amplitudeLoadPromise: Promise<any> | null = null;

let initOnce = false;
let clientPromise: Promise<AmplitudeClient | null> | null = null;

function canTrack(): boolean {
	if (typeof window === 'undefined') return false;
	if (!ENV.AMPLITUDE_ID) return false;
	return Boolean(getChosenCookies('analytics'));
}

async function loadAmplitude(): Promise<any> {
	if (amplitudeMod) return amplitudeMod;

	if (!amplitudeLoadPromise) {
		amplitudeLoadPromise = import('amplitude-js').then((m: any) => {
			amplitudeMod = m?.default ?? m;
			return amplitudeMod;
		});
	}

	return await amplitudeLoadPromise;
}

async function getClient(): Promise<AmplitudeClient | null> {
	if (!canTrack()) return null;

	if (clientPromise) return await clientPromise;

	clientPromise = (async () => {
		const amp = await loadAmplitude();
		const client = amp.getInstance() as AmplitudeClient;

		if (!initOnce) {
			initOnce = true;

			client.init(ENV.AMPLITUDE_ID as string, '', {});
		}

		return client;
	})();

	return await clientPromise;
}

// Fallback transport used when the client SDK is not running (analytics not
// granted / GPC / geo opt-out — i.e. canTrack() is false). Sends the event
// straight to Amplitude's HTTP API via ApiService.logDirectEvent, mirroring the
// SDK path's identity: device_id from the uuid cookie, synchronously-derived
// user properties, and the stored user id.
function sendDirect(
	eventType: string,
	eventProperties?: Record<string, unknown>,
) {
	if (typeof window === 'undefined') return;
	if (!ENV.AMPLITUDE_ID) return;

	const deviceId = CookiesStorageService.get<string>(COOKIES_KEYS.UUID, '');
	if (!deviceId) return;

	const userId = LocalStorageService.get<string | undefined>(
		LOCAL_STORAGE_KEYS.USER_ID,
		undefined,
	);

	void ApiService.logDirectEvent(
		eventType,
		deviceId,
		{ ...eventProperties, 'current url': window.location.href },
		getAnalyticsUserProperties(),
		userId,
	);
}

export const AmplitudeService = {
	initialization(callback?: (client: AmplitudeClient) => void) {
		void getClient().then(client => {
			if (!client) return;
			callback?.(client);
		});
	},

	setDeviceId(deviceId: string) {
		void getClient().then(client => {
			if (!client) return;
			client.setDeviceId(deviceId);
		});
	},

	setUserId(userId: string | null) {
		void getClient().then(client => {
			if (!client) return;
			client.setUserId(userId);
		});
	},

	setUserProperty(properties: Record<string, unknown>) {
		void getClient().then(client => {
			if (!client) return;
			client.setUserProperties(properties);
		});
	},

	logEvent(eventType: string, eventProperties?: object, callback?: Callback) {
		if (typeof window === 'undefined') return;

		// SDK not running → deliver via the direct HTTP API instead. Still invoke
		// the callback: some callers redirect from it (e.g. INSTALL_PRESSED →
		// window.location on the Success page), which must work under opt-out.
		if (!canTrack()) {
			sendDirect(eventType, eventProperties as Record<string, unknown>);
			callback?.(200, 'sendDirect');
			return;
		}

		void getClient().then(client => {
			if (!client) return;

			client.logEvent(
				eventType,
				{
					...eventProperties,
					'current url': window.location.href,
				},
				callback,
				() => console.log(`Amplitude Error in ${eventType} event`),
			);
		});
	},

	// Sends an event straight to Amplitude's HTTP API via a keepalive request, so
	// it survives an immediate navigation (e.g. CTA redirect to the funnel) that
	// would otherwise cancel the in-flight SDK request. Works even before the
	// SDK has initialized — the device_id is read synchronously from the cookie.
	//
	// User properties are attached to the same guaranteed-delivery request: the
	// SDK's async $identify call (used by setUserProperty) is gated behind SDK
	// init and gets cancelled by the redirect, so properties like utmCampaign
	// were often lost. getAnalyticsUserProperties() reads them synchronously
	// (URL/cookies/localStorage), and Amplitude applies an event's
	// user_properties as a $set on the user.
	logEventViaBeacon(
		eventType: string,
		eventProperties?: Record<string, unknown>,
		userProperties?: Record<string, unknown>,
		/**
		 * Pass an insert_id to enable Amplitude's built-in dedup. The cookie
		 * handoff (landing → funnel) reuses this same id on the re-send, so
		 * Amplitude sees the second copy as a duplicate and drops it silently.
		 */
		insertId?: string,
		/**
		 * Called only on a 2xx response from Amplitude. The pending-event cookie
		 * handler uses this to clear the cookie once delivery is confirmed.
		 */
		onSuccess?: () => void,
	) {
		if (typeof window === 'undefined') return;

		// No canTrack() gate: this is already a direct HTTP-API post (the same
		// transport as logDirectEvent, plus keepalive so it survives the CTA
		// redirect). It must fire on every landing even when the SDK is off
		// (analytics not granted / GPC / geo opt-out).
		const deviceId = CookiesStorageService.get<string>(COOKIES_KEYS.UUID, '');
		if (!deviceId) return;

		const sessionId = amplitudeMod?.getInstance?.()?.getSessionId?.() ?? -1;
		const resolvedUserProperties =
			userProperties ?? getAnalyticsUserProperties();

		const body = JSON.stringify({
			api_key: ENV.AMPLITUDE_ID,
			events: [
				{
					device_id: deviceId,
					session_id: sessionId,
					event_type: eventType,
					time: Date.now(),
					platform: 'Web',
					event_properties: {
						...eventProperties,
						'current url': window.location.href,
					},
					user_properties: resolvedUserProperties,
					...(insertId ? { insert_id: insertId } : {}),
				},
			],
		});

		postToAmplitudeApi(body, `${eventType} event`, onSuccess);
	},

	// Sets Amplitude user properties via a guaranteed-delivery $identify call to
	// the HTTP API. Mirrors setUserProperty but, like logEventViaBeacon, survives
	// an immediate navigation and does not depend on the SDK being initialized —
	// so values like utmCampaign land on non-landing entry pages (e.g. onboarding
	// reached straight from an ad) where the async SDK $identify could be lost.
	setUserPropertyViaBeacon(properties: Record<string, unknown>) {
		if (typeof window === 'undefined') return;
		if (!canTrack()) return;

		const deviceId = CookiesStorageService.get<string>(COOKIES_KEYS.UUID, '');
		if (!deviceId) return;

		const sessionId = amplitudeMod?.getInstance?.()?.getSessionId?.() ?? -1;

		const body = JSON.stringify({
			api_key: ENV.AMPLITUDE_ID,
			events: [
				{
					device_id: deviceId,
					session_id: sessionId,
					event_type: '$identify',
					time: Date.now(),
					platform: 'Web',
					user_properties: { $set: properties },
				},
			],
		});

		postToAmplitudeApi(body, '$identify');
	},

	async logEventAsync(
		eventType: string,
		eventProperties?: Record<string, unknown>,
	): Promise<void> {
		if (typeof window === 'undefined') return;

		// SDK not running → deliver via the direct HTTP API instead.
		if (!canTrack()) {
			sendDirect(eventType, eventProperties);
			return;
		}

		const client = await getClient();
		if (!client) return;

		await new Promise<void>(resolve => {
			client.logEvent(
				eventType,
				{
					...eventProperties,
					'current url': window.location.href,
				},
				() => resolve(),
				() => resolve(),
			);
		});
	},

	logEventWithoutDuplication(
		eventType: string,
		eventProperties?: Record<string, unknown>,
		callback?: Callback,
	) {
		if (typeof window === 'undefined') return;

		const time = 20 * 60 * 1000; // 20 minutes in milliseconds
		const currentTime = Date.now();
		const timeFromStorage = sessionStorage.getItem(eventType);
		const elapsedTime = currentTime - parseInt(timeFromStorage ?? '', 10);

		// If there is no time in storage or the elapsed time is greater than 20 minutes, then log the event
		if (!timeFromStorage || elapsedTime > time) {
			sessionStorage.setItem(eventType, currentTime.toString());

			// SDK not running → deliver via the direct HTTP API instead. Invoke the
			// callback too, so callers that redirect from it still work under opt-out.
			if (!canTrack()) {
				sendDirect(eventType, eventProperties);
				callback?.(200, 'sendDirect');
				return;
			}

			void getClient().then(client => {
				if (!client) return;

				client.logEvent(
					eventType,
					{
						...eventProperties,
						'current url': window.location.href,
					},
					callback,
					() => console.log(`Amplitude Error in ${eventType} event`),
				);
			});
		}
	},
};
